import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import HelpModal from '../HelpModal';
import { FaPlay, FaPause, FaVolumeUp, FaVolumeMute } from 'react-icons/fa';
import HEXASLogo from '../assets/Logos/HEXASLogo.jpg';
import { useTextHighlight } from '../utils/highlightUtils';

const Class10GuidedPractice01 = () => {
  const navigate = useNavigate();

  const [showHelp, setShowHelp] = useState(false);

  const questionRefs = useRef([]);
  const rightColRef = useRef(null);

  // Make sure your answers state exists
  const [answers, setAnswers] = useState(Array(20).fill(''));
  const [currentQuestion, setCurrentQuestion] = useState(14);
  const onNext = () => {
    setCurrentQuestion(prev => Math.min(prev + 1, 19));
  };

  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);

  // Timer state
  const [timeRemaining, setTimeRemaining] = useState(100 * 60); // 30 minutes in seconds
  const [showSeconds, setShowSeconds] = useState(false);

  // Timer effect
  useEffect(() => {
    const timer = setInterval(() => {
      setTimeRemaining(prev => {
        if (prev <= 0) {
          clearInterval(timer);
          navigate('/');  // Redirect to home
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [navigate]);

  // Format time function
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;

    if (showSeconds) {
      return `${mins} : ${secs.toString().padStart(2, '0')} remaining`;
    }
    return `${mins} minutes remaining`;
  };

  const handleStartTest = () => {
    navigate(`/guided-practice-01-form`);
  };

  const handlePlayPause = () => {
    if (!audioRef.current) return;
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  const handleTimeUpdate = () => {
    if (!audioRef.current) return;
    setCurrentTime(audioRef.current.currentTime);
  };

  const handleLoadedMetadata = () => {
    if (!audioRef.current) return;
    setDuration(audioRef.current.duration);
  };

  const handleSeek = (e) => {
    if (!audioRef.current) return;
    const time = parseFloat(e.target.value);
    audioRef.current.currentTime = time;
    setCurrentTime(time);
  };

  const handleVolumeChange = (e) => {
    if (!audioRef.current) return;
    const vol = parseFloat(e.target.value);
    setVolume(vol);
    audioRef.current.volume = vol;
    setIsMuted(vol === 0);
  };

  const handleMute = () => {
    if (!audioRef.current) return;
    if (isMuted) {
      audioRef.current.muted = false;
      setIsMuted(false);
      audioRef.current.volume = volume;
    } else {
      audioRef.current.muted = true;
      setIsMuted(true);
    }
  };

  const formatAudioTime = (t) => {
    if (isNaN(t)) return '00:00';
    const m = Math.floor(t / 60);
    const s = Math.floor(t % 60);
    return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };

  const GreenCheck = () => (
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="inline-block"
    >
      <path
        d="M7 11L9 13.5L16 7"
        stroke="#7DD77D"
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );

  const highlightRef = useRef(null);
  useTextHighlight(highlightRef);


  return (
    <div className="min-h-screen bg-white flex flex-col font-[Arial]">
      {/* Help Modal */}
      <HelpModal
        isOpen={showHelp}
        onClose={() => setShowHelp(false)}
      />

      {/* Top Bar (fixed) */}
      <div className="sticky top-0 z-30 bg-white">
        <div className="relative h-[62px] bg-[#F4C3C3] border-b border-black flex items-center justify-between px-4">
          {/* Left + Center combined on left side */}
          <div className="flex flex-col justify-center space-y-0.5 pl-6">
            <span className="text-[14px] text-left text-black font-bold leading-tight">
              HEXA'S Education
            </span>
            <div className="flex items-center space-x-6 text-[14px] text-black font-medium">
              <span
                onMouseEnter={() => setShowSeconds(true)}
                onMouseLeave={() => setShowSeconds(false)}
                className="cursor-pointer"
              >
                {formatTime(timeRemaining)}
              </span>
            </div>
          </div>

          {/* Right: Icons and Finish Button */}
          <div className="flex items-center space-x-5">
            <button
              onClick={() => navigate('/')}
              className="text-black border border-black rounded-md px-4 py-1 text-[14px]  hover:bg-gray-100"
            >
              Back Home
            </button>
            <button
              onClick={() => setShowHelp(true)}
              className="text-black border border-black rounded-md px-4 py-1 text-[14px]  hover:bg-gray-100"
            >
              Finish test
            </button>

            {/* Icons container with larger size and spacing */}
            <div className="flex items-center space-x-8 pl-2 pr-2">
              {/* WiFi Icon */}
              <div className="w-10 h-10 flex items-center justify-center">
                <svg className="w-9 h-9 text-black" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M17.778 8.222c-4.296-4.296-11.26-4.296-15.556 0A1 1 0 010.808 6.808c5.076-5.077 13.308-5.077 18.384 0a1 1 0 01-1.414 1.414zM14.95 11.05a7 7 0 00-9.9 0 1 1 0 01-1.414-1.414 9 9 0 0112.728 0 1 1 0 01-1.414 1.414zM12.12 13.88a3 3 0 00-4.24 0 1 1 0 01-1.415-1.415 5 5 0 017.07 0 1 1 0 01-1.415 1.415zM9 16a1 1 0 011-1h.01a1 1 0 110 2H10a1 1 0 01-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>

              {/* Notification Bell Icon */}
              <div className="w-10 h-10 flex items-center justify-center">
                <svg className="w-8 h-8 text-black" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M10 2a6 6 0 00-6 6v3.586l-.707.707A1 1 0 004 14h12a1 1 0 00.707-1.707L16 11.586V8a6 6 0 00-6-6zM10 18a3 3 0 01-3-3h6a3 3 0 01-3 3z" />
                </svg>
              </div>

              {/* Hamburger Menu Icon */}
              <div className="w-10 h-10 flex items-center justify-center">
                <svg className="w-8 h-8 text-black" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M3 5a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 10a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 15a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>

              {/* Pen/Edit Icon */}
              <div className="w-10 h-10 flex items-center justify-center">
                <svg className="w-8 h-8 text-black" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                </svg>
              </div>
            </div>
          </div>
        </div>

        <div className="relative bg-[#F4F7FA] shadow-sm border border-black rounded-lg mx-12 mt-4 mb-0 sticky top-[32px] z-20 flex items-center justify-between">
          {/* Left: Part Title */}
          <div className="flex-1 px-4 py-2 text-left">
            <h2 className="text-[23px] text-black">Part 1</h2>
            <h1 className="text-[18px] text-black">HEXA'S Reading Course Book</h1>
          </div>

          {/* Center: Absolutely Positioned Logo */}
          <div className="absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2">
            <img
              src={HEXASLogo}
              alt="HEXA'S Logo"
              className="h-[50px] object-contain"
            />
          </div>

          {/* Right: Empty space for consistency */}
          <div className="flex items-center justify-end w-[300px] pr-4">
            {/* Empty space - no audio player for reading */}
          </div>
        </div>
      </div>

      {/* Main content (scrollable) */}
      <div className="flex-1 flex flex-col items-center justify-start w-full">
        {/* Two-Column Main Content Area */}
        <div ref={highlightRef}
          className="flex flex-row w-full overflow-hidden -mb-16 mt-2 px-12 pb-20"
          style={{ height: 'calc(100vh - 180px)' }}
        >
          {/* Left: Reading Passage */}
          <div className="bg-[#F4F7FA] rounded-md shadow border border-gray-300 p-6 w-[49%] h-full max-h-full overflow-y-auto">
            <div className="mb-8">
              <h2 className="text-[16px] font-bold mb-4 text-center">Space: The Final Archaeological Frontier</h2>
              <h3 className="text-[14px] font-semibold mb-6 text-center italic">
                Space travel may still have a long way to go, but the notion of archaeological research and heritage management in space is already concerning scientists and environmentalists.
              </h3>

              {/* Remaining paragraphs - left aligned */}
              <div className="text-[14px] space-y-4 text-left">
                <p>
                  In 1993, University of Hawaii’s anthropologist Ben Finney, who for much of his career has studied the technology once used by Polynesians to colonize islands in the Pacific, suggested that it would not be premature to begin thinking about the archaeology of Russian and American aerospace sites on the Moon and Mars. Finney pointed out that just as today’s scholars use archaeological records to investigate how Polynesians diverged culturally as they explored the Pacific, archaeologists will someday study off-Earth sites to trace the development of humans in space. He realized that it was unlikely anyone would be able to conduct fieldwork in the near future, but he was convinced that one day such work would be done.
                </p>
                <p>
                  There is a growing awareness, however, that it won’t be long before both corporate adventurers and space tourists reach the Moon and Mars. There is a wealth of important archaeological sites from the history of space exploration on the Moon and Mars and measures need to be taken to protect these sites. In addition to the threat from profit-seeking corporations, scholars cite other potentially destructive forces such as souvenir hunting and unmonitored scientific sampling, as has already occurred in explorations of remote polar regions. Already in 1999 one company was proposing a robotic lunar rover mission beginning at the site of Tranquillity Base and rumbling across the Moon from one archaeological site to another, from the wreck of the Ranger S probe to Apollo 17’s landing site. The mission, which would leave vehicle tyre-marks all over some of the most famous sites on the Moon, was promoted as a form of theme-park entertainment.
                </p>
                <p>
                  According to the vaguely worded United Nations Outer Space Treaty of 1967, what it terms ‘space junk’ remains the property of the country that sent the craft or probe into space. But the treaty doesn’t explicitly address protection of sites like Tranquillity Base, and equating the remains of human exploration of the heavens with ‘space junk’ leaves them vulnerable to scavengers. Another problem arises through other international treaties proclaiming that land in space cannot be owned by any country or individual. This presents some interesting dilemmas for the aspiring manager of extraterrestrial cultural resources. Does the US own Neil Armstrong's famous first footprints on the Moon but not the lunar dust in which they were recorded? Surely those footprints are as important in the story of human development as those left by hominids at Laetoli, Tanzania. But unlike the Laetoli prints, which have survived for 3.5 million years encased in cement-like ash, those at Tranquillity Base could be swept away with a casual brush of a space tourist’s hand. To deal with problems like these, it may be time to look to innovative international administrative structures for the preservation of historic remains on the new frontier.
                </p>
                <p>
                  The Moon, with its wealth of sites, will surely be the first destination of archaeologists trained to work in space. But any young scholars hoping to claim the mantle of history’s first lunar archaeologist will be disappointed. That distinction is already taken.
                </p>
                <p>
                  On November 19, 1969, astronauts Charles Conrad and Alan Bean made a difficult manual landing of the Apollo 12 lunar module in the Moon’s Ocean of Storms, just a few hundred feet from an unmanned probe, Surveyor 3, that had landed in a crater on April 19, 1967. Unrecognized at the time, this was an important moment in the history of science. Bean and Conrad were about to conduct the first archaeological studies on the Moon.
                </p>
                <p>
                  After the obligatory planting of the American flag and some geological sampling, Conrad and Bean made their way to Surveyor 3. They observed that the probe had bounced after touchdown and carefully photographed the impressions made by its footpads. The whole spacecraft was covered in dust, perhaps kicked up by the landing.
                </p>
                <p>
                  The astronaut-archaeologists carefully removed the probe’s television camera, remote sampling arm, and pieces of tubing. They bagged and labelled these artefacts, and stowed them on board their lunar module. On their return to Earth, they passed them on to the Daveson Space Centre in Houston, Texas, and the Hughes Air and Space Corporation in El Segundo, California. There, scientists analysed the changes in these aerospace artefacts.
                </p>
                <p>
                  One result of the analysis astonished them. A fragment of the television camera revealed evidence of the bacteria Streptococcus mitis. For a moment it was thought Conrad and Bean had discovered evidence for life on the Moon, but after further research the real explanation became apparent. While the camera was being installed in the probe prior to the launch, someone sneezed on it. The resulting bacteria had travelled to the Moon, remained in an alternating freezing, boiling vacuum for more than two years, and returned promptly to life upon reaching the safety of a laboratory back on Earth.
                </p>
                <p>
                  The finding that not even the vastness of space can stop humans from spreading a sore throat was an unexpected spin-off. But the artefacts brought back by Bean and Conrad have a broader significance. Simple as they may seem, they provide the first example of extraterrestrial archaeology and perhaps more significant for the history of the discipline — formational archaeology, the study of environmental and cultural forces upon the life history of human artefacts in space.
                </p>
              </div>
            </div>
          </div>






          {/* Right: Flowchart Questions */}
          <div
            className="w-[50%] flex flex-col items-start bg-[#F4F7FA] rounded-md shadow border border-gray-300 p-6 h-full max-h-full overflow-y-auto relative"
            style={{ overflowAnchor: 'none' }} // Optional: prevents jumping
          >
            {/* Questions Header */}
            <div className="mb-6 w-full">
              <h3 className="text-[15px] font-bold mb-3 text-left">Questions 34 – 38</h3>
              <p className="text-[14px] mb-4 text-left">
                Complete the flow chart below. <br />
                Write <strong>ONE WORD ONLY</strong> from the passage for each answer.
              </p>
            </div>

            {/* Flowchart */}
            <div className="flex flex-col items-center text-[14px] text-black space-y-3 w-full">
              {/* Step 1 */}
              <div className="w-full bg-[#E3E9F0] text-center border border-[#A0A9B8] rounded-lg p-4 shadow-sm">
                During the assembly of the Surveyor 3 probe, someone
                <span
                  className="inline-block align-middle rounded bg-[#E0F2FE] border border-[#7DD3FC] px-1 py-1 ml-2 mr-2 shadow"
                  style={{ minWidth: 105, minHeight: 26 }}
                >
                  <input
                    ref={el => questionRefs.current[33] = el}
                    type="text"
                    placeholder="34"
                    className="w-full h-full bg-transparent text-center text-[13px] font-semibold outline-none placeholder:text-black focus:placeholder:opacity-0"
                    value={answers[33] || ""}
                    onChange={e => {
                      const newAnswers = [...answers];
                      newAnswers[33] = e.target.value;
                      setAnswers(newAnswers);
                    }}
                  />
                </span>
                on a TV camera.
              </div>
              <div className="text-[32px] font-bold text-gray-600 leading-none">↓</div>

              {/* Step 2 */}
              <div className="w-full bg-[#E3E9F0] text-center border border-[#A0A9B8] rounded-lg p-4 shadow-sm">
                The TV Camera was carried to the Moon on Surveyor 3
              </div>
              <div className="text-[32px] font-bold text-gray-600 leading-none">↓</div>

              {/* Step 3 */}
              <div className="w-full bg-[#E3E9F0] text-center border border-[#A0A9B8] rounded-lg p-4 shadow-sm">
                The TV Camera remained on the Moon for over
                <span
                  className="inline-block align-middle rounded bg-[#E0F2FE] border border-[#7DD3FC] px-1 py-1 ml-2 mr-2 shadow"
                  style={{ minWidth: 105, minHeight: 26 }}
                >
                  <input
                    ref={el => questionRefs.current[34] = el}
                    type="text"
                    placeholder="35"
                    className="w-full h-full bg-transparent text-center text-[13px] font-semibold outline-none placeholder:text-black focus:placeholder:opacity-0"
                    value={answers[34] || ""}
                    onChange={e => {
                      const newAnswers = [...answers];
                      newAnswers[34] = e.target.value;
                      setAnswers(newAnswers);
                    }}
                  />
                </span>
                years
              </div>
              <div className="text-[32px] font-bold text-gray-600 leading-none">↓</div>

              {/* Step 4 */}
              <div className="w-full bg-[#E3E9F0] text-center border border-[#A0A9B8] rounded-lg p-4 shadow-sm">
                Apollo 12 astronauts
                <span
                  className="inline-block align-middle rounded bg-[#E0F2FE] border border-[#7DD3FC] px-1 py-1 ml-2 mr-2 shadow"
                  style={{ minWidth: 105, minHeight: 26 }}
                >
                  <input
                    ref={el => questionRefs.current[35] = el}
                    type="text"
                    placeholder="36"
                    className="w-full h-full bg-transparent text-center text-[13px] font-semibold outline-none placeholder:text-black focus:placeholder:opacity-0"
                    value={answers[35] || ""}
                    onChange={e => {
                      const newAnswers = [...answers];
                      newAnswers[35] = e.target.value;
                      setAnswers(newAnswers);
                    }}
                  />
                </span>
                the TV camera
              </div>
              <div className="text-[32px] font-bold text-gray-600 leading-none">↓</div>

              {/* Step 5 */}
              <div className="w-full bg-[#E3E9F0] text-center border border-[#A0A9B8] rounded-lg p-4 shadow-sm">
                The TV camera was returned to Earth for
                <span
                  className="inline-block align-middle rounded bg-[#E0F2FE] border border-[#7DD3FC] px-1 py-1 ml-2 mr-2 shadow"
                  style={{ minWidth: 105, minHeight: 26 }}
                >
                  <input
                    ref={el => questionRefs.current[36] = el}
                    type="text"
                    placeholder="37"
                    className="w-full h-full bg-transparent text-center text-[13px] font-semibold outline-none placeholder:text-black focus:placeholder:opacity-0"
                    value={answers[36] || ""}
                    onChange={e => {
                      const newAnswers = [...answers];
                      newAnswers[36] = e.target.value;
                      setAnswers(newAnswers);
                    }}
                  />
                </span>
              </div>
              <div className="text-[32px] font-bold text-gray-600 leading-none">↓</div>

              {/* Step 6 */}
              <div className="w-full bg-[#E3E9F0] text-center border border-[#A0A9B8] rounded-lg p-4 shadow-sm">
                The theory that this suggested there was
                <span
                  className="inline-block align-middle rounded bg-[#E0F2FE] border border-[#7DD3FC] px-1 py-1 ml-2 mr-2 shadow"
                  style={{ minWidth: 105, minHeight: 26 }}
                >
                  <input
                    ref={el => questionRefs.current[37] = el}
                    type="text"
                    placeholder="38"
                    className="w-full h-full bg-transparent text-center text-[13px] font-semibold outline-none placeholder:text-black focus:placeholder:opacity-0"
                    value={answers[37] || ""}
                    onChange={e => {
                      const newAnswers = [...answers];
                      newAnswers[37] = e.target.value;
                      setAnswers(newAnswers);
                    }}
                  />
                </span>
                on the Moon was rejected.
              </div>
            </div>
          </div>








        </div>
      </div>

      {/* Bottom Navigation (fixed footer) */}
      <div className="sticky bottom-0 left-0 w-full bg-white border-t border-gray-300 z-30 flex items-center justify-start px-4 py-3">
        {/* New Navigation Bar */}
        <div className="flex items-center max-w-2xl">
          {/* Part Label with bar above */}
          <div className="flex flex-col items-center mr-4">
            <div className="h-1 w-12 mb-1 rounded-full bg-gray-300" />
            <span className="text-[15px] font-bold text-gray-700">Part 1</span>
          </div>
          {/* Numbers with lines above */}
          <div className="flex items-end space-x-2">
            {[34, 35, 36, 37, 38].map((num) => {
              const isActive = currentQuestion === num;
              const isAnswered = answers[num - 1] && answers[num - 1] !== '';
              return (
                <div key={num} className="flex flex-col items-center">
                  {/* Line above number */}
                  <div
                    className="h-1 w-7 mb-1 rounded-full"
                    style={{ backgroundColor: isAnswered ? '#C9A842' : '#D1D5DB' }}
                  ></div>

                  {/* Number button */}
                  <button
                    onClick={() => {
                      setCurrentQuestion(num);
                      const input = questionRefs.current[num - 1];
                      if (input) {
                        input.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        input.focus();
                      }
                    }}
                    className={`w-7 h-7 flex items-center justify-center text-[15px] font-bold
                ${isActive ? 'border-2 border-sky-400 bg-white text-sky-700 rounded-md' : 'text-gray-700'}
                transition-colors duration-150`}
                    style={{ boxShadow: isActive ? '0 0 0 0px #38bdf8 inset' : 'none' }}
                  >
                    {num}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      </div>








    </div>
  );
};

export default Class10GuidedPractice01;
